import { streamRng } from '../math/rng.js';
import { BRAIN, STATE } from '../actors/ai.js';
import { createFollowers } from './Followers.js';

/**
 * @file THE MISSION DIRECTOR: what the player is supposed to be doing, where
 * they have to stand to be told, and whether they managed it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE STATES, AND ONLY ONE MISSION IS EVER ACTIVE
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 *   LOCKED ──requirements met──► AVAILABLE ──player stands in the giver ring──►
 *   BRIEFING ──cutscene ends──► ACTIVE ──last stage passes──► COMPLETE
 *                                  │
 *                                  └──a fail rule trips──► FAILED ──cooldown──►
 *                                                          AVAILABLE (retry)
 * ```
 * Several missions can be AVAILABLE at once — that is what the marker set and
 * the mission list are for — but exactly one can be ACTIVE, which is the genre's
 * rule and also the only way "the objective" means anything on a one-line HUD
 * panel. Taking a second briefing while one is active is refused at the ring
 * (the marker still draws, dimmed).
 *
 * TRACKING is separate from ACTIVE: the player chooses which mission the panel
 * and the highlighted marker follow. It defaults to the active one, then to the
 * nearest critical available one, so a player who never opens the list is
 * always being pointed somewhere useful.
 *
 * A def may carry a `reward`, paid into the INJECTED wallet
 * (`systems/Wallet.js`) on the transition into COMPLETE and on no other. There
 * is no partial credit and no consolation for a fail; the "exactly once" is
 * structural rather than guarded, because `complete` is the single door every
 * pass goes through and a COMPLETE mission is never stepped again.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A STAGE IS A CONDITION PLUS A MARKER, AND THE CONDITION IS ONE OF FIVE
 * ─────────────────────────────────────────────────────────────────────────────
 * | kind        | passes when                                  | fails when |
 * |-------------|----------------------------------------------|------------|
 * | `goto`      | the player is inside `radius` of the point    | timer      |
 * | `eliminate` | every hostile this stage spawned is dead      | timer      |
 * | `chase`     | the player gets within `catchRadius` of the target, or kills it | the target passes `escapeRadius`, or timer |
 * | `protect`   | `duration` seconds elapse                    | a protected ally dies, or timer |
 * | `wait`      | `duration` seconds elapse                    | timer      |
 *
 * `goto` additionally takes `requireCar` / `requireFoot`, which is how "drive
 * there" and "get out and walk in" are expressed without a sixth kind. A
 * drive-by mission is NOT here and cannot be until cars have a combat model —
 * see CLAUDE.md.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WORLD POINTS ARE RESOLVED ONCE, FROM THE MAP, THROUGH A SEEDED STREAM
 * ─────────────────────────────────────────────────────────────────────────────
 * A mission def does NOT carry coordinates: the city is procedural, so a
 * hard-coded corner is a corner that exists on one seed. Instead each def
 * exports `place(ctx)` and gets the level's map, the territory lookup, the
 * pavement graph, the player's spawn and a DEDICATED rng stream
 * (`'missions.<id>'`, rule 4 — one stream per mission, so adding a mission
 * cannot move an existing one). It returns a bag of named points, and every
 * stage refers to them by name. Same seed, same city, same mission.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CONTENT IS INJECTED — THE ENGINE NEVER IMPORTS `src/game/`
 * ─────────────────────────────────────────────────────────────────────────────
 * Same seam as `game/factions.js` and `game/enemies/`: `PlayRoute` builds a
 * {@link MissionScript} and hands it to `createGameplay({missions})`. Without
 * one, not a line of this file runs and gameplay is exactly what it was.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS
 * ─────────────────────────────────────────────────────────────────────────────
 * Manager, population, traffic, driving, followers and a `teleport` CALLBACK are
 * all injected, so the whole thing — the ring test, every objective kind, the
 * spawn budget, the fail rules, the marker feed — is proved headless in
 * `tests/missions.test.mjs`. The one thing that genuinely needs a GPU is the
 * glowing ring on the tarmac, and that is `render/MissionMarkers.js` reading
 * {@link createMissions}'s `markers()`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * | channel                 | dir | payload |
 * |-------------------------|-----|---------|
 * | `mission:list`          | out | `{missions: [...], active, tracked}` ON CHANGE |
 * | `mission:objective`     | out | `{id, name, text, stage, stages, critical, state}` ON CHANGE |
 * | `mission:cutscene`      | out | `{token, id, shots: [{image, speaker, text}]}` |
 * | `mission:cutscene:end`  | in  | `{token}` — the HUD, when the player has read it |
 * | `mission:track`         | in  | `{id}` — the mission list's toggle |
 * | `mission:request`       | in  | (no payload) — republish both channels NOW |
 * | `mission:event`         | out | `{type: 'started'|'complete'|'failed', id, name, critical}` |
 * | `mission:offer`         | out | `{id, name, critical, call}` — ONCE per job, per session |
 * | `player:wasted`         | in  | fails an active mission that says so |
 *
 * ⚠ `mission:offer` IS ITS OWN CHANNEL AND MUST NOT BE FOLDED INTO
 * `mission:event`. `ui/hud/MissionPanel.jsx`'s banner draws ANY payload carrying
 * a `type`, and its ternary falls through to `'Mission started'` — so an
 * `{type: 'offered'}` would print **MISSION STARTED** across the middle of the
 * screen the moment a job merely unlocked. The three event types are
 * `started|complete|failed` and that set is closed.
 *
 * ⚠ AND IT IS QUEUED, NOT EMITTED WHERE IT HAPPENS. `relock()` runs from inside
 * `createMissions()` itself, before `Gameplay` has finished constructing — the
 * phone does not exist yet and the bus has no replay, so the intro's offer would
 * be emitted into an empty room, for ever, with no error. That is this header's
 * "'on change' and 'mounts late' do not mix", one line further up. Offers are
 * therefore pushed onto {@link offerQ} and drained at the END of the first
 * `update`, AFTER `publish()` — so a subscriber reacting to an offer has already
 * been handed the mission list the offer belongs to — which also makes
 * construction ORDER irrelevant.
 *
 * ⚠ `mission:request` EXISTS BECAUSE "ON CHANGE" AND "MOUNTS LATE" DO NOT MIX.
 * The mission list is a pause-menu screen: it mounts long after the last change
 * was published, and the pause menu has already set the loop's time scale to
 * zero — so no step runs, no change happens, and a component that only listens
 * would render an empty list forever. (Found exactly that way in the browser.)
 * The handler publishes SYNCHRONOUSLY from the bus callback, which is why it
 * works while the world is frozen: a bus emit is a function call, not a tick.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A MISSION HOSTILE HAS TO BE MADE HOSTILE — SPAWNING IT IS NOT ENOUGH
 * ─────────────────────────────────────────────────────────────────────────────
 * This cost a playtest, and it is the least obvious thing in the file. A gang's
 * hostility toward the PLAYER is not in the static matrix: it is a 45 s timer
 * (`FactionState#provoke`) that only starts when the player hurts one of them.
 * That is exactly right for the ambient city — it is what makes a street feel
 * like a street — and it is exactly wrong for a mission, because:
 *
 *  • `EnemyManager` only considers the player a target for a faction that
 *    `ctx.playerHostile()` approves, so the crew you were sent to fight walks
 *    straight past you; and
 *  • `Gameplay#pickTarget` filters to currently-hostile factions, so your gun
 *    stays silent at them too.
 *
 * The result is an `eliminate` stage that cannot be cleared BY ANY MEANS AT
 * ALL. ⚠ That is stronger than it was when this was written: there used to be
 * an escape hatch (holding SPACE, the deliberate 120° attack), and the note
 * here said only that it was impossible in a car. The deliberate attack is
 * DELETED as of 2026-08-07 — see `Gameplay.js`'s header — so this provocation
 * is now the ONLY thing standing between a mission and an unwinnable stage.
 * A `role: 'hostile'` spawn PROVOKES its faction, and {@link update}
 * REFRESHES that provocation for as
 * long as any of them is alive, because 45 s is shorter than a firefight and a
 * crew that stops caring halfway through is worse than one that never started.
 *
 * ⚠ It provokes a FACTION, not a slot — that is the only granularity the team
 * byte has — so the Greens you were sent to hit and the Greens two blocks away
 * turn together. That is the honest reading: you drove into their turf. And
 * ⚠ `provoke` is a no-op for anything that is not a gang; POLICE hostility
 * belongs to `systems/Wanted.js` and a mission must not forge a wanted level.
 *
 * ⚠ `mission:cutscene` carries a TOKEN and the director only accepts an end
 * event carrying the same one. A cutscene that is still on screen when the
 * player dies, or when the level is torn down, would otherwise resume a mission
 * that no longer exists — and a HUD that fires its end event twice would skip a
 * stage.
 *
 * @module engine/systems/Missions
 */

/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('./Population.js').FactionRules} FactionRules */

/**
 * The game's mission content, injected. `src/game/missions/index.js` is the
 * shipped implementation.
 *
 * @typedef {Object} MissionScript
 * @property {MissionDef[]} missions In offer order. Ids must be unique.
 */

/**
 * @typedef {Object} MissionDef
 * @property {string} id
 * @property {string} name Display title.
 * @property {string} [blurb] One line for the mission list.
 * @property {boolean} [critical=false] Story mission (vs optional side work).
 * @property {string[]} [requires] Mission ids that must be COMPLETE first.
 * @property {number} [faction] Faction id, for the marker colour and the giver's
 *   allegiance. Purely presentational here.
 * @property {(ctx: PlaceCtx) => Object<string, {x: number, z: number}>} [place]
 *   Resolves the mission's named world points. Called ONCE at install.
 * @property {string} [giverAt='giver'] Which named point the pickup ring sits on.
 * @property {number} [giverRadius=3.2] The ring, metres.
 * @property {string} [giverLabel] One or two characters drawn in the map blip.
 * @property {CutsceneDef} [cutscene] Played on pickup, before stage 0.
 * @property {Object} [call] The PHONE CALL that offers this job, if it gets one.
 *   Carried on `'mission:offer'` BY IDENTITY and never read here — the shape
 *   belongs to `systems/Phone.js` (`PhoneCallDef`: a caller, a portrait and a
 *   list of shots), and this director's only relationship with it is that it
 *   passes the reference on. A def without one still publishes its offer: the
 *   FACT that a job came on offer is the director's, and having nothing to say
 *   about it is the phone's business.
 * @property {string} [teleport] Named point the player is moved to once that
 *   cutscene ends. THE time-skip — see {@link createMissions}'s note.
 * @property {SpawnDef[]} [spawn] Actors spawned when the mission starts.
 * @property {StageDef[]} stages At least one.
 * @property {boolean} [failOnWasted=true] Dying fails an active mission.
 * @property {boolean} [failOnAllyDown=false] Losing a teammate fails it.
 * @property {number} [timeLimit=0] Seconds for the whole mission; 0 = none.
 * @property {number} [retryDelay=6] Seconds a FAILED mission waits before it is
 *   offered again.
 * @property {number} [reward=0] Money paid into the injected wallet when the
 *   mission COMPLETES. Never paid on a fail, never paid twice — a mission
 *   reaches {@link createMissions}'s `complete` once per run, and a completed
 *   one is never re-offered. Absent (or without a wallet) is simply no payout.
 */

/**
 * @typedef {Object} StageDef
 * @property {string} [id]
 * @property {string} text The HUD line. Keep it to a sentence.
 * @property {'goto'|'eliminate'|'chase'|'protect'|'wait'} kind
 * @property {string} [at] Named point this stage's marker and tests use.
 * @property {number} [radius=8] `goto`'s arrival radius, metres.
 * @property {boolean} [requireCar=false] `goto` only passes while driving.
 * @property {boolean} [requireFoot=false] ...or only on foot.
 * @property {number} [catchRadius=4] `chase`'s catch distance.
 * @property {number} [escapeRadius=95] `chase` fails past this.
 * @property {number} [duration=0] `wait`/`protect` length, seconds.
 * @property {number} [timeLimit=0] Seconds for this stage; 0 = none.
 * @property {boolean} [marker=true] Draw a world/map marker for it.
 * @property {SpawnDef[]} [spawn] Actors spawned when the stage STARTS.
 * @property {CutsceneDef} [cutscene] Played when the stage starts.
 * @property {string} [teleport] Named point to move the player to after it.
 */

/**
 * @typedef {Object} SpawnDef
 * @property {'hostile'|'ally'|'target'} role `hostile` counts toward
 *   `eliminate`; `ally` joins the escort and can trip `failOnAllyDown`;
 *   `target` is the thing a `chase` is chasing.
 * @property {string[]} ids Def ids to draw from (uniformly, seeded).
 * @property {number} [count=1]
 * @property {number} faction Faction id written to the `team` byte.
 * @property {string} [at] Named point to spawn around; defaults to the player.
 * @property {number} [spread=6] Metres of scatter about that point.
 * @property {number} [speedMul=1] Per-slot speed scale — a chase target's legs.
 * @property {boolean} [provoke] Should this group be hostile to THE PLAYER?
 *   Defaults to TRUE for `role: 'hostile'` and false for everything else — see
 *   {@link createMissions}'s note on why a mission has to say so out loud.
 *   Set it false for a `hostile` group the player is meant to watch fight
 *   somebody else rather than fight themselves.
 * @property {boolean} [flee=false] Spawn it as a permanently frightened
 *   civilian: this is what a runner IS (see the `chase` note below).
 *
 * ⚠ A RUNNER'S `faction` SHOULD ALMOST ALWAYS BE THE NEUTRAL ONE, whatever crew
 * the fiction says he belongs to. `flee` sets `BRAIN.CIVILIAN`, and a civilian
 * brain is still a legal TARGET — `EnemyManager#_acquire` skips only
 * `BRAIN.SEATED` — so a Purple runner bolting through Red turf gets shot by the
 * ambient Reds long before the player closes, and the chase "completes" without
 * the player doing anything. Watched happen in the browser. The def's ART still
 * says which crew he is; his TEAM BYTE just says he is nobody's fight.
 */

/**
 * @typedef {Object} CutsceneDef
 * @property {string} [id]
 * @property {Array<{image?: string, speaker?: string, text: string}>} shots
 */

/**
 * @typedef {Object} PlaceCtx
 * @property {any} map `level.map`.
 * @property {(x: number, z: number) => number} territoryOf
 * @property {any} pavement The population's waypoint graph (may be null).
 * @property {{x: number, z: number}} spawn The player's start.
 * @property {import('../types.js').Bounds2|null} bounds
 * @property {(x: number, z: number) => number} heightAt
 * @property {import('../types.js').Rng} rng This mission's dedicated stream.
 */

/** Mission lifecycle. Stored as a string because it is published to the HUD. */
export const MISSION_STATE = Object.freeze({
  LOCKED: 'locked',
  AVAILABLE: 'available',
  BRIEFING: 'briefing',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  FAILED: 'failed',
});

/** Everything about how missions are offered and policed. */
export const MISSIONS = Object.freeze({
  /** Default pickup ring, metres. Big enough to walk into by accident, which is
   *  the point — a marker you have to aim at is a marker you walk past. */
  giverRadius: 3.2,
  /** Default `goto` arrival radius. */
  gotoRadius: 8,
  /** Default `chase` catch / escape distances. */
  catchRadius: 4,
  escapeRadius: 95,
  /** Seconds a FAILED mission waits before it is offered again. */
  retryDelay: 6,
  /**
   * WORLD markers past this distance are not published for the 3D ring batch.
   * The map still shows every one of them — a marker on the tarmac 300 m away
   * is a glowing dot inside a building, and the map is what long-range
   * navigation is for.
   */
  worldMarkerRange: 140,
  /** Hard cap on world markers, so the ring batch's draw never grows. */
  worldMarkerMax: 8,
  /**
   * Metres a mission actor may drift from the player before it is culled. WELL
   * outside `POP.despawn` (95) — a mission enemy that despawns because you
   * backed off is a mission that cannot be completed. 0 disables the cull.
   */
  actorCull: 260,
  /**
   * A `chase` target is re-frightened every this many seconds. `EnemyManager#scare`
   * runs a 6 s flight timer and re-aims the bearing on each call, so calling it
   * on a slow cadence is what keeps a runner running AWAY FROM THE PLAYER
   * rather than in the direction they first bolted.
   */
  fleeRefresh: 0.4,
});

const TAU = Math.PI * 2;

/**
 * @param {Object} o
 * @param {EnemyManager} o.manager
 * @param {Array<{id?: string}>} o.types The manager's type table.
 * @param {MissionScript} o.script THE CONTENT — see the header.
 * @param {FactionRules} o.factions
 * @param {{position: {x: number, y: number, z: number}, velocity: {x: number, z: number}, dead?: boolean}} o.player
 * @param {any} [o.map] `level.map`, for the placement context.
 * @param {any} [o.population] The ambient director — its pavement graph and
 *   `territoryOf` go into the placement context, and its `alarm` is used when a
 *   mission drops a crew into a street.
 * @param {any} [o.traffic]
 * @param {any} [o.driving]
 * @param {{emit: Function, on?: Function}} [o.bus]
 * @param {any} [o.collision]
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {(x: number, z: number, yaw?: number) => void} [o.teleport] Moves the
 *   PLAYER. Injected because the player actor and the camera rig both live in
 *   `Gameplay.js`, which imports three.js and this file must not.
 * @param {{x: number, z: number}} [o.spawn] The player's level start.
 * @param {any} [o.wallet] `systems/Wallet.js`. Injected like everything else
 *   this file touches, so the director stays provable in node — and OPTIONAL,
 *   because a mission's `reward` is content and the money system is not this
 *   file's dependency. Without one nothing is paid and nothing throws.
 * @param {string|number} [o.seed='gameplay']
 * @param {Partial<typeof MISSIONS>} [o.tune]
 * @returns {Object}
 */
export function createMissions(o) {
  const {
    manager,
    types = [],
    script,
    factions,
    player: player0,
    map = null,
    population = null,
    traffic = null,
    driving = null,
    bus = null,
    collision = null,
    heightAt = () => 0,
    bounds = null,
    teleport = null,
    spawn = { x: 0, z: 0 },
    wallet = null,
    seed = 'gameplay',
    tune = null,
  } = o ?? {};

  if (!manager) throw new Error('createMissions: a manager is required');
  if (!script?.missions?.length) throw new Error('createMissions: a MissionScript with missions is required');
  if (!factions) throw new Error('createMissions: a FactionRules table is required');
  if (!player0) throw new Error('createMissions: a player is required');

  const M = tune ? { ...MISSIONS, ...tune } : MISSIONS;
  /** Re-pointable: `Gameplay` rebuilds the `Player` when its sprite decodes. */
  let player = player0;

  const followers = createFollowers({
    manager, player, traffic, driving, collision, heightAt, bounds,
  });

  /** @param {string} id @returns {number} Type index, or -1 when not loaded. */
  const typeOf = (id) => types.findIndex((t) => t?.id === id);

  // ── the books ──────────────────────────────────────────────────────────────

  /**
   * @typedef {Object} MissionActor
   * @property {number} slot
   * @property {number} team The re-validation key against slot recycling —
   *   `Wanted.js#ours`'s trick, and for the same reason.
   * @property {'hostile'|'ally'|'target'} role
   * @property {boolean} provoke Keep this actor's faction hostile to the player
   *   while it lives — see the header. Per ACTOR rather than per spawn group so
   *   the refresh pass has everything it needs in the one array it already
   *   walks.
   * @property {boolean} flee A runner: re-scared on {@link MISSIONS.fleeRefresh}.
   * @property {number} fleeT
   * @property {boolean} down Set once, when it dies — so `eliminate` counts a
   *   kill exactly once even though a slot frees a fifth of a second later.
   */

  /**
   * @typedef {Object} Mission
   * @property {MissionDef} def
   * @property {string} state {@link MISSION_STATE}
   * @property {number} stage Index into `def.stages`, -1 before the first.
   * @property {Object<string, {x: number, z: number}>} points Resolved once.
   * @property {MissionActor[]} actors Everything this mission put in the world.
   * @property {number} clock Seconds in the current stage.
   * @property {number} total Seconds since the mission started.
   * @property {number} retryT Seconds since it failed.
   * @property {number} killed Hostiles down in the current stage.
   * @property {number} quota Hostiles the current stage spawned.
   * @property {string} cutsceneToken Non-empty while a cutscene is on screen.
   * @property {boolean} offered Has this job EVER been on offer? A retry is not
   *   a new offer, so the phone rings once per job per session.
   */
  /** @type {Mission[]} */
  const missions = [];

  let clock = 0;
  let disposed = false;
  let activeId = '';
  let trackedId = '';
  let nextToken = 1;
  let started = 0;
  let completed = 0;
  let failed = 0;
  /** Set by any state change; the publish happens once, at the end of the step. */
  let dirty = true;

  const probe = { x: 0, z: 0 };

  // ── placement ──────────────────────────────────────────────────────────────

  const placeCtxBase = {
    map,
    territoryOf: population?.territoryOf ?? (() => factions.CIVILIAN),
    pavement: population?.pavement ?? null,
    spawn,
    bounds,
    heightAt,
  };

  for (const def of script.missions) {
    if (!def?.id || !def.stages?.length) {
      console.warn('[missions] skipping a def with no id or no stages', def?.id);
      continue;
    }
    // ONE STREAM PER MISSION (rule 4). Adding a mission to the script must not
    // move where an existing one puts its hideout.
    const rng = streamRng(seed, `missions.${def.id}`);
    let points = {};
    try {
      points = def.place ? def.place({ ...placeCtxBase, rng }) ?? {} : {};
    } catch (err) {
      console.warn(`[missions] ${def.id}: place() threw, falling back to the player spawn`, err);
      points = {};
    }
    missions.push({
      def,
      state: MISSION_STATE.LOCKED,
      stage: -1,
      points,
      actors: [],
      clock: 0,
      total: 0,
      retryT: 0,
      killed: 0,
      quota: 0,
      cutsceneToken: '',
      offered: false,
    });
  }

  /**
   * OFFERS PENDING PUBLICATION. ⚠ A QUEUE RATHER THAN A DIRECT EMIT — see the
   * header: `relock()` is called from inside this constructor, and an emit there
   * lands in an empty room.
   * @type {Mission[]}
   */
  const offerQ = [];

  /**
   * ⚠ THE ONE DOOR INTO `MISSION_STATE.AVAILABLE`. It had two writers —
   * `relock()` and the FAILED retry branch — and "the phone rings once per job"
   * was going to be a property of those two agreeing rather than of the code.
   * The `offered` latch lives HERE, so a retry after a failure re-offers the
   * mission to the WORLD (the marker comes back, the giver ring works) without
   * ringing anybody: you already took that call.
   * @param {Mission} m
   */
  const offer = (m) => {
    m.state = MISSION_STATE.AVAILABLE;
    dirty = true;
    if (m.offered) return;
    m.offered = true;
    offerQ.push(m);
  };

  /**
   * A mission's named point, falling back to the player's level start so a def
   * with a broken `place()` is a mission you can still walk to rather than a
   * mission at the origin (which, on this map, is the sea).
   * @param {Mission} m @param {string|undefined} name
   * @returns {{x: number, z: number}}
   */
  const pointOf = (m, name) => {
    const p = name ? m.points[name] : null;
    return p && Number.isFinite(p.x) && Number.isFinite(p.z) ? p : spawn;
  };

  // ── actors ─────────────────────────────────────────────────────────────────

  /**
   * Is this still OUR actor? Same re-validation `Wanted.js#ours` runs, and for
   * the same reason: a slot frees on death and the ambient director can refill
   * it inside one step.
   * @param {MissionActor} a
   */
  const ours = (a) => a.slot >= 0 && manager.isLive(a.slot) && manager.team[a.slot] === a.team;

  /**
   * Runs one {@link SpawnDef}. Positions are scattered on a seeded disc about
   * the named point and pushed out of the collision grid; a spawn that cannot
   * find room simply produces fewer actors, which every objective below is
   * written to survive (an `eliminate` quota counts what was ACTUALLY spawned).
   *
   * @param {Mission} m @param {SpawnDef} s @param {import('../types.js').Rng} rng
   * @returns {number} How many landed.
   */
  const runSpawn = (m, s, rng) => {
    const pool = (s.ids ?? []).map(typeOf).filter((i) => i >= 0);
    if (!pool.length) {
      console.warn(`[missions] ${m.def.id}: none of ${JSON.stringify(s.ids)} is in the roster`);
      return 0;
    }
    const at = s.at ? pointOf(m, s.at) : player.position;
    const spread = s.spread ?? 6;
    const count = Math.max(1, s.count ?? 1);
    let n = 0;
    for (let k = 0; k < count; k++) {
      const ti = pool[Math.min(pool.length - 1, (rng() * pool.length) | 0)];
      // Golden-angle scatter over the disc: even coverage without rejection
      // sampling, and the rng draw above stays one per actor.
      const a = rng() * TAU;
      const r = spread * Math.sqrt((k + 0.5) / count);
      probe.x = at.x + Math.sin(a) * r;
      probe.z = at.z + Math.cos(a) * r;
      const rad = manager.types[ti]?.radius ?? 0.4;
      if (bounds) {
        probe.x = Math.min(Math.max(probe.x, bounds.minX + rad), bounds.maxX - rad);
        probe.z = Math.min(Math.max(probe.z, bounds.minZ + rad), bounds.maxZ - rad);
      }
      if (collision?.resolveCircle) {
        const res = collision.resolveCircle(probe, rad, 2);
        probe.x = res.x;
        probe.z = res.z;
      }
      const slot = manager.spawn(ti, probe.x, probe.z, heightAt(probe.x, probe.z), s.faction);
      if (slot < 0) break;
      if (s.speedMul && s.speedMul !== 1) manager.setSpeedMul(slot, s.speedMul);
      const role = s.role ?? 'hostile';
      /** @type {MissionActor} */
      const rec = {
        slot,
        team: manager.team[slot],
        role,
        // Hostiles are the player's problem by default; allies and chase
        // targets never are. See the header for why this is not automatic.
        provoke: s.provoke ?? role === 'hostile',
        flee: !!s.flee,
        fleeT: M.fleeRefresh,
        down: false,
      };
      // A RUNNER IS A FRIGHTENED CIVILIAN. `BRAIN.CIVILIAN` + a refreshed
      // `scare` is a full-speed flight away from the player that already knows
      // how to path round the collision grid — and it never shoots back, which
      // is what a chase is. Nothing new was needed for it.
      if (rec.flee) manager.setBrain(slot, BRAIN.CIVILIAN);
      if (rec.role === 'ally') followers.add(slot, m.def.id);
      // Hostile from the step it lands, not from the step the player finally
      // works out how to hit it — see the header.
      if (rec.provoke) factions.state.provoke?.(rec.team);
      m.actors.push(rec);
      n++;
    }
    // A crew appearing in a street is something the neighbourhood notices.
    population?.alarm?.(at.x, at.z, 10);
    return n;
  };

  /**
   * Frees everything a mission put in the world. `keepSurvivors` leaves the
   * ALLIES standing (a completed mission's teammates walking off reads far
   * better than a squad blinking out beside you) and only clears the hostiles
   * and the chase target.
   * @param {Mission} m @param {boolean} keepSurvivors
   */
  const clearActors = (m, keepSurvivors) => {
    followers.release(m.def.id);
    // ⚠ ITERATE A SNAPSHOT. `manager.despawn` fires `onDespawn`, which `Gameplay`
    // wires straight back into {@link onSlotFreed} — and that SPLICES the very
    // array we are walking. A plain `for…of` therefore skips every other actor,
    // which reads as "dispose leaked half the mission" and sends you looking in
    // the wrong file. `tests/missions.test.mjs` section (m) pins it.
    const doomed = m.actors.slice();
    m.actors.length = 0;
    for (const a of doomed) {
      if (!ours(a)) continue;
      if (keepSurvivors && a.role === 'ally') {
        // Back to the world: no leash, an ordinary member of his crew again.
        manager.clearHome(a.slot);
        continue;
      }
      manager.despawn(a.slot);
    }
  };

  // ── cutscenes ──────────────────────────────────────────────────────────────

  /**
   * Puts a cutscene on screen and parks the mission until the HUD says the
   * player has read it. With no bus (a headless test that did not wire one) the
   * scene is skipped rather than hung — a mission that cannot start without a
   * React component is a mission the engine cannot be tested on.
   *
   * @param {Mission} m @param {CutsceneDef|null|undefined} cut
   * @returns {boolean} True when a cutscene is now playing.
   */
  const playCutscene = (m, cut) => {
    if (!cut?.shots?.length || !bus?.emit) return false;
    m.cutsceneToken = `${m.def.id}#${nextToken++}`;
    bus.emit('mission:cutscene', {
      token: m.cutsceneToken,
      id: cut.id ?? m.def.id,
      mission: m.def.id,
      name: m.def.name,
      shots: cut.shots,
    });
    return true;
  };

  // ── stages ─────────────────────────────────────────────────────────────────

  /**
   * Enters stage `k`. Spawns its actors, plays its cutscene, and performs its
   * teleport — in that order, because the teleport is meant to land the player
   * AT the thing the cutscene just described.
   *
   * ⚠ The teleport is deliberately BEFORE the cutscene finishes rather than
   * after: the screen is covered, so the move is invisible, which is the whole
   * trick. Doing it on the end event instead makes the world jump under a
   * player who is already looking at it.
   *
   * @param {Mission} m @param {number} k
   */
  const enterStage = (m, k) => {
    m.stage = k;
    m.clock = 0;
    m.killed = 0;
    m.quota = 0;
    const st = m.def.stages[k];
    if (!st) return;
    const rng = streamRng(seed, `missions.${m.def.id}.stage${k}`);
    for (const s of st.spawn ?? []) {
      const n = runSpawn(m, s, rng);
      if ((s.role ?? 'hostile') === 'hostile') m.quota += n;
    }
    playCutscene(m, st.cutscene);
    if (st.teleport) doTeleport(m, st.teleport);
    dirty = true;
  };

  /** @param {Mission} m @param {string} name */
  const doTeleport = (m, name) => {
    if (typeof teleport !== 'function') return;
    const p = pointOf(m, name);
    teleport(p.x, p.z);
  };

  /** @param {Mission} m */
  const complete = (m) => {
    m.state = MISSION_STATE.COMPLETE;
    m.stage = m.def.stages.length;
    clearActors(m, true);
    if (activeId === m.def.id) activeId = '';
    completed++;
    // THE PAYOUT, and this is the only line in the file that touches money. It
    // is HERE rather than in the caller because `complete` is the single door
    // every pass goes through — a mission whose last stage passes and one that
    // runs off the end of its stage list both arrive here, and neither can
    // arrive twice (the state is COMPLETE above, and a COMPLETE mission is
    // never offered, started or stepped again). `fail` has no counterpart on
    // purpose: you are not paid for a job you lost.
    if (m.def.reward > 0) wallet?.add?.(m.def.reward);
    dirty = true;
    bus?.emit?.('mission:event', {
      type: 'complete', id: m.def.id, name: m.def.name, critical: !!m.def.critical,
    });
    // Anything this mission unlocked can be offered on the next step.
    relock();
  };

  /**
   * Takes a cutscene DOWN from this side. The HUD closes on its own when the
   * player finishes reading, so this is only for the cases the player did not
   * cause — a mission failing under a briefing, a teardown — and without it the
   * still image is stranded on screen over a frozen loop with nothing left to
   * answer it.
   *
   * ⚠ The token is cleared BEFORE the emit, so the director's own
   * `'mission:cutscene:end'` handler finds no match and the call cannot recurse.
   * @param {Mission} m
   */
  const cancelCutscene = (m) => {
    if (!m.cutsceneToken) return;
    const token = m.cutsceneToken;
    m.cutsceneToken = '';
    bus?.emit?.('mission:cutscene:end', { token, cancelled: true });
  };

  /** @param {Mission} m @param {string} reason */
  const fail = (m, reason) => {
    m.state = MISSION_STATE.FAILED;
    m.retryT = 0;
    cancelCutscene(m);
    clearActors(m, false);
    if (activeId === m.def.id) activeId = '';
    failed++;
    dirty = true;
    bus?.emit?.('mission:event', {
      type: 'failed', id: m.def.id, name: m.def.name, critical: !!m.def.critical, reason,
    });
  };

  /** @param {Mission} m */
  const start = (m) => {
    m.state = MISSION_STATE.ACTIVE;
    m.total = 0;
    activeId = m.def.id;
    if (!trackedId || !byId(trackedId) || byId(trackedId)?.state !== MISSION_STATE.ACTIVE) trackedId = m.def.id;
    started++;
    const rng = streamRng(seed, `missions.${m.def.id}.open`);
    for (const s of m.def.spawn ?? []) runSpawn(m, s, rng);
    bus?.emit?.('mission:event', {
      type: 'started', id: m.def.id, name: m.def.name, critical: !!m.def.critical,
    });
    enterStage(m, 0);
  };

  /** @param {string} id @returns {Mission|null} */
  const byId = (id) => missions.find((m) => m.def.id === id) ?? null;

  /** Re-evaluates every LOCKED mission's requirements. Cheap; runs on change. */
  const relock = () => {
    const done = new Set(missions.filter((m) => m.state === MISSION_STATE.COMPLETE).map((m) => m.def.id));
    for (const m of missions) {
      if (m.state !== MISSION_STATE.LOCKED) continue;
      const reqs = m.def.requires ?? [];
      if (reqs.every((r) => done.has(r))) offer(m);
    }
  };

  // ── objective evaluation ───────────────────────────────────────────────────

  /**
   * One stage, one step. Returns `'pass'`, `'fail'` or `''` (still running).
   * PURE apart from `m.killed`, which is a read-out rather than state.
   * @param {Mission} m @param {StageDef} st
   * @returns {string}
   */
  const evalStage = (m, st) => {
    const px = player.position.x;
    const pz = player.position.z;

    if (st.timeLimit > 0 && m.clock >= st.timeLimit) return 'fail';

    switch (st.kind) {
      case 'wait':
        return m.clock >= (st.duration ?? 0) ? 'pass' : '';

      case 'protect': {
        // The ally-down rule below is what actually fails this; the stage's own
        // job is only to hold the objective open for `duration` seconds.
        for (const a of m.actors) if (a.role === 'ally' && a.down) return 'fail';
        return m.clock >= (st.duration ?? 0) ? 'pass' : '';
      }

      case 'eliminate': {
        // Count what is DOWN rather than what is left: a hostile that walked out
        // of the world is not a hostile you killed, and `m.quota` is what the
        // spawn actually produced.
        let down = 0;
        let live = 0;
        for (const a of m.actors) {
          if (a.role !== 'hostile') continue;
          if (a.down) down++;
          else if (ours(a)) live++;
        }
        m.killed = down;
        return live === 0 ? 'pass' : '';
      }

      case 'chase': {
        const target = m.actors.find((a) => a.role === 'target');
        if (!target) return 'pass';
        // Killing the runner ends the chase too — a player who shoots him has
        // caught him by any reading that matters.
        if (target.down) return 'pass';
        if (!ours(target)) return 'pass';
        const dx = manager.x[target.slot] - px;
        const dz = manager.z[target.slot] - pz;
        const d2 = dx * dx + dz * dz;
        const catchR = st.catchRadius ?? M.catchRadius;
        if (d2 <= catchR * catchR) return 'pass';
        const esc = st.escapeRadius ?? M.escapeRadius;
        if (d2 > esc * esc) return 'fail';
        return '';
      }

      default: {
        // `goto`. The vehicle gate is checked FIRST so standing in the ring on
        // foot when the stage wanted a car does not silently pass.
        if (st.requireCar && !driving?.inCar) return '';
        if (st.requireFoot && driving?.inCar) return '';
        const at = pointOf(m, st.at);
        const r = st.radius ?? M.gotoRadius;
        const dx = at.x - px;
        const dz = at.z - pz;
        return dx * dx + dz * dz <= r * r ? 'pass' : '';
      }
    }
  };

  // ── the step ───────────────────────────────────────────────────────────────

  /**
   * One fixed step.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed || !(dt > 0)) return;
    clock += dt;

    // 1. actor bookkeeping, in ONE pass per mission: mark the dead, run the
    //    runners, cull the strays.
    for (const m of missions) {
      if (!m.actors.length) continue;
      for (let i = m.actors.length - 1; i >= 0; i--) {
        const a = m.actors[i];
        const live = ours(a);
        // DYING, not "the slot freed": the shrink is a fifth of a second long
        // and an objective that waits it out reads as lag. `down` latches, so a
        // recycled slot can never un-kill somebody.
        if (!a.down && (!live || manager.state[a.slot] === STATE.DYING)) {
          a.down = true;
          dirty = true;
        }
        if (!live) continue;
        // ⚠ REFRESH THE PROVOCATION, every step, for as long as this one is
        // alive. `FACTION_TUNING.provokeSeconds` is 45 and a mission firefight
        // can outlast that easily; a crew that stops caring halfway through an
        // `eliminate` is worse than one that never started. `provoke` is a
        // single array write, so a cadence here would be more bookkeeping than
        // it saves. It decays naturally once the last of them is down, which is
        // the "you just shot up a crew" tail we want anyway.
        if (a.provoke) factions.state.provoke?.(a.team);
        if (a.flee) {
          a.fleeT += dt;
          if (a.fleeT >= M.fleeRefresh) {
            a.fleeT = 0;
            // Re-aimed AWAY FROM THE PLAYER every time — that is the difference
            // between a runner and a civilian who bolted once and kept going.
            manager.scare(a.slot, player.position.x, player.position.z);
          }
        }
        if (M.actorCull > 0 && a.role !== 'ally') {
          const dx = manager.x[a.slot] - player.position.x;
          const dz = manager.z[a.slot] - player.position.z;
          if (dx * dx + dz * dz > M.actorCull * M.actorCull) {
            manager.despawn(a.slot);
            m.actors.splice(i, 1);
          }
        }
      }
    }

    // 2. the escort.
    followers.update(dt);

    // 3. per-mission state.
    for (const m of missions) {
      switch (m.state) {
        case MISSION_STATE.FAILED: {
          m.retryT += dt;
          if (m.retryT >= (m.def.retryDelay ?? M.retryDelay)) {
            // ⚠ Through the same door, and the `offered` latch inside it is what
            // makes a RETRY silent: the job is on the map again, but the phone
            // does not ring a second time for a call you have already had.
            offer(m);
            m.stage = -1;
          }
          break;
        }

        case MISSION_STATE.AVAILABLE: {
          // The pickup ring. Refused while another mission is running — the
          // marker still draws (dimmed) so the player can see what is waiting.
          if (activeId) break;
          if (player.dead) break;
          const at = pointOf(m, m.def.giverAt ?? 'giver');
          const r = m.def.giverRadius ?? M.giverRadius;
          const dx = at.x - player.position.x;
          const dz = at.z - player.position.z;
          if (dx * dx + dz * dz > r * r) break;
          // ⚠ YOU GET OUT OF THE CAR TO TAKE A JOB, and that is load-bearing
          // rather than staging. A briefing taken through a windscreen leaves
          // the player driving when the crew spawns beside them — and an escort
          // boards on the EDGE of getting into a car, so a crew that appears
          // while you are already in one can never get in (owner playtest: you
          // had to get out and back in to collect your own teammates).
          //
          // Fixing it in `Followers.js` was tried and is a closed negative
          // result — see its header. Fixing it HERE costs one line and is the
          // better reading anyway: you do not take orders from a gang leader
          // through a car window.
          //
          // `force` skips the speed gate on purpose. The alternative above
          // `DRIVE.exitSpeed` is a BAIL — the player dumped on the tarmac while
          // the car coasts off — which is the wrong way to arrive at a job.
          // Forcing parks it where it stands, a few metres from the marker, and
          // the player walks back to it when the mission says to drive.
          if (driving?.inCar) driving.exitCar(true);
          // BRIEFING is a real state, not a flag: a mission whose cutscene is
          // on screen has been taken (nothing else can start) but has not begun
          // (no stage timer runs, nothing has spawned).
          m.state = MISSION_STATE.BRIEFING;
          activeId = m.def.id;
          trackedId = m.def.id;
          dirty = true;
          if (!playCutscene(m, m.def.cutscene)) {
            if (m.def.teleport) doTeleport(m, m.def.teleport);
            start(m);
          }
          break;
        }

        case MISSION_STATE.BRIEFING:
          // Waiting on `'mission:cutscene:end'`. Nothing ticks here on purpose
          // — with ONE exception. A player who is dead cannot be read a
          // briefing, and a mission parked in BRIEFING is a mission that has
          // taken the ACTIVE slot and will never give it back: nothing else can
          // be started, and the answer it is waiting for is never coming.
          // (The HUD freezes the loop, so this is normally unreachable — but a
          // scene with no cutscene component, or a death on the very step the
          // ring was entered, both get here.)
          if ((m.def.failOnWasted ?? true) && player.dead) fail(m, 'wasted');
          break;

        case MISSION_STATE.ACTIVE: {
          m.clock += dt;
          m.total += dt;

          // Mission-wide fail rules, before the stage's own condition: a
          // mission that failed this step must not also pass a stage on it.
          if ((m.def.failOnWasted ?? true) && player.dead) {
            fail(m, 'wasted');
            break;
          }
          if (m.def.failOnAllyDown && m.actors.some((a) => a.role === 'ally' && a.down)) {
            fail(m, 'ally');
            break;
          }
          if (m.def.timeLimit > 0 && m.total >= m.def.timeLimit) {
            fail(m, 'time');
            break;
          }
          // A cutscene mid-mission holds the stage exactly as the briefing does.
          if (m.cutsceneToken) break;

          const st = m.def.stages[m.stage];
          if (!st) {
            complete(m);
            break;
          }
          const verdict = evalStage(m, st);
          if (verdict === 'fail') {
            fail(m, st.kind);
          } else if (verdict === 'pass') {
            if (m.stage + 1 >= m.def.stages.length) complete(m);
            else enterStage(m, m.stage + 1);
          }
          break;
        }

        default:
          break;
      }
    }

    // 4. tracking + publish. ONE emit per change, never per step.
    const tracked = byId(trackedId);
    if (!tracked || tracked.state === MISSION_STATE.COMPLETE || tracked.state === MISSION_STATE.LOCKED) {
      const next = pickTracked();
      if (next !== trackedId) {
        trackedId = next;
        dirty = true;
      }
    }
    if (dirty) publish();

    // 5. THE OFFERS, drained LAST — after `publish()`, so a subscriber that
    //    reacts to an offer (the phone rings, and a ring is a HUD change) has
    //    already been handed the mission list that offer belongs to. One emit
    //    per job per session; the `offered` latch inside {@link offer} is what
    //    guarantees it, so this loop needs no bookkeeping of its own.
    while (offerQ.length) {
      const m = offerQ.shift();
      bus?.emit?.('mission:offer', {
        id: m.def.id,
        name: m.def.name,
        critical: !!m.def.critical,
        // ⚠ BY IDENTITY, never copied. It is content this file does not read;
        // a shallow copy here would silently drop whatever a call grows next.
        call: m.def.call ?? null,
      });
    }
  };

  /**
   * Which mission the HUD should be pointing at when the player has not chosen:
   * the active one, else the nearest CRITICAL available one, else the nearest
   * available one at all.
   * @returns {string}
   */
  const pickTracked = () => {
    if (activeId) return activeId;
    let best = '';
    let bestKey = Infinity;
    for (const m of missions) {
      if (m.state !== MISSION_STATE.AVAILABLE) continue;
      const at = pointOf(m, m.def.giverAt ?? 'giver');
      const dx = at.x - player.position.x;
      const dz = at.z - player.position.z;
      // Critical missions sort ahead of optional ones whatever the distance —
      // the story is what a player who has not chosen wants pointing at.
      const key = Math.sqrt(dx * dx + dz * dz) + (m.def.critical ? 0 : 1e5);
      if (key < bestKey) {
        bestKey = key;
        best = m.def.id;
      }
    }
    return best;
  };

  // ── publishing ─────────────────────────────────────────────────────────────

  /** The tracked mission's current objective line, or ''. */
  const objectiveText = () => {
    const m = byId(trackedId);
    if (!m) return '';
    if (m.state === MISSION_STATE.AVAILABLE) return m.def.blurb ?? `Go to ${m.def.name}.`;
    if (m.state === MISSION_STATE.BRIEFING) return m.def.blurb ?? '';
    if (m.state !== MISSION_STATE.ACTIVE) return '';
    const st = m.def.stages[m.stage];
    if (!st) return '';
    if (st.kind === 'eliminate' && m.quota > 0) {
      const left = Math.max(0, m.quota - m.killed);
      return `${st.text} (${left} left)`;
    }
    return st.text;
  };

  /** Fresh payloads — these are retained by React state, unlike `'hud:blips'`. */
  const publish = () => {
    dirty = false;
    if (!bus?.emit) return;
    const list = missions.map((m) => {
      const at = pointOf(m, m.def.giverAt ?? 'giver');
      return {
        id: m.def.id,
        name: m.def.name,
        blurb: m.def.blurb ?? '',
        critical: !!m.def.critical,
        state: m.state,
        stage: m.stage,
        stages: m.def.stages.length,
        text: m.state === MISSION_STATE.ACTIVE ? m.def.stages[m.stage]?.text ?? '' : m.def.blurb ?? '',
        tracked: m.def.id === trackedId,
        x: at.x,
        z: at.z,
      };
    });
    bus.emit('mission:list', { missions: list, active: activeId, tracked: trackedId });

    const m = byId(trackedId);
    bus.emit('mission:objective', m
      ? {
        id: m.def.id,
        name: m.def.name,
        text: objectiveText(),
        state: m.state,
        stage: Math.max(0, m.stage),
        stages: m.def.stages.length,
        critical: !!m.def.critical,
      }
      : null);
  };

  // ── markers ────────────────────────────────────────────────────────────────

  /**
   * WHERE THE PLAYER HAS TO GO, for both the map and the 3D ring batch. Reused
   * entries and a reused array, because this is read every frame by the marker
   * batch and eight times a second by the blip feed — the same no-litter rule
   * `Gameplay`'s blip payload follows.
   * @type {Array<{x: number, z: number, r: number, kind: string, tracked: boolean,
   *   label: string, id: string, dim: boolean}>}
   */
  const markerPool = Array.from({ length: 32 }, () => ({
    x: 0, z: 0, r: 0, kind: 'objective', tracked: false, label: '', id: '', dim: false,
  }));
  /** @type {typeof markerPool} */
  const markerList = [];
  /** Scratch for a marker that tracks a live actor. Module-lifetime, like the
   *  pool — `markers()` is called once per FRAME by the ring batch. */
  const markerAt = { x: 0, z: 0 };

  /**
   * @returns {typeof markerList} REUSED — copy what you keep.
   */
  const markers = () => {
    let n = 0;
    for (const m of missions) {
      if (n >= markerPool.length) break;
      const e = markerPool[n];
      if (m.state === MISSION_STATE.AVAILABLE) {
        const at = pointOf(m, m.def.giverAt ?? 'giver');
        e.x = at.x;
        e.z = at.z;
        e.r = m.def.giverRadius ?? M.giverRadius;
        e.kind = m.def.critical ? 'giver' : 'giver_optional';
        e.label = m.def.giverLabel ?? (m.def.critical ? '!' : '?');
        // Dimmed while another mission is running: you can see it, you cannot
        // take it. That reading is worth more than hiding it.
        e.dim = !!activeId;
      } else if (m.state === MISSION_STATE.ACTIVE) {
        const st = m.def.stages[m.stage];
        if (!st || st.marker === false) continue;
        // A `chase` marker follows the RUNNER, which is the only honest place
        // to point: the point he started from stopped being useful the moment
        // he left it.
        let at = pointOf(m, st.at);
        if (st.kind === 'chase') {
          const t = m.actors.find((a) => a.role === 'target');
          if (t && ours(t)) {
            markerAt.x = manager.x[t.slot];
            markerAt.z = manager.z[t.slot];
            at = markerAt;
          }
        } else if (st.kind === 'eliminate') {
          // ...and an `eliminate` marker sits on the CENTROID of what is left,
          // so "clear the block" points at the last two men rather than at the
          // spot the fight started.
          let sx = 0;
          let sz = 0;
          let k = 0;
          for (const a of m.actors) {
            if (a.role !== 'hostile' || a.down || !ours(a)) continue;
            sx += manager.x[a.slot];
            sz += manager.z[a.slot];
            k++;
          }
          if (k) {
            markerAt.x = sx / k;
            markerAt.z = sz / k;
            at = markerAt;
          }
        }
        e.x = at.x;
        e.z = at.z;
        e.r = st.kind === 'goto' ? st.radius ?? M.gotoRadius : Math.max(3, st.catchRadius ?? M.catchRadius);
        e.kind = 'objective';
        e.label = '';
        e.dim = false;
      } else {
        continue;
      }
      e.tracked = m.def.id === trackedId;
      e.id = m.def.id;
      markerList[n++] = e;
    }
    markerList.length = n;
    return markerList;
  };

  /**
   * The subset the 3D ring batch draws: near the player, nearest first, capped.
   * The MAP gets the whole list — a glowing ring 300 m away is inside a
   * building, and long-range navigation is what a map is for.
   * @param {Array<any>} out REUSED by the caller.
   * @returns {Array<any>} `out`.
   */
  const worldMarkers = (out) => {
    const all = markers();
    const px = player.position.x;
    const pz = player.position.z;
    const r2 = M.worldMarkerRange * M.worldMarkerRange;
    out.length = 0;
    for (const e of all) {
      const dx = e.x - px;
      const dz = e.z - pz;
      if (dx * dx + dz * dz > r2) continue;
      out.push(e);
      if (out.length >= M.worldMarkerMax) break;
    }
    return out;
  };

  // ── bus ────────────────────────────────────────────────────────────────────

  const offs = [];
  if (bus?.on) {
    offs.push(bus.on('mission:cutscene:end', (p) => {
      if (disposed) return;
      const token = typeof p === 'string' ? p : p?.token;
      const m = missions.find((x) => x.cutsceneToken && x.cutsceneToken === token);
      // An unmatched token is a stale HUD (a cutscene that outlived its mission,
      // a double-fire). Ignoring it is what stops a stage from being skipped.
      if (!m) return;
      m.cutsceneToken = '';
      if (m.state === MISSION_STATE.BRIEFING) {
        if (m.def.teleport) doTeleport(m, m.def.teleport);
        start(m);
      }
      dirty = true;
    }));
    // A late subscriber asking for the current state — see the header. Straight
    // to `publish()`, not a `dirty` flag: the caller may well be a paused HUD,
    // and nothing will tick to drain the flag.
    offs.push(bus.on('mission:request', () => {
      if (!disposed) publish();
    }));
    offs.push(bus.on('mission:track', (p) => {
      if (disposed) return;
      const id = typeof p === 'string' ? p : p?.id;
      const m = id ? byId(id) : null;
      if (!m || m.state === MISSION_STATE.LOCKED || m.state === MISSION_STATE.COMPLETE) return;
      trackedId = m.def.id;
      // ⚠ PUBLISH, NOT `dirty = true` — the same reason the handler three lines
      // above spells out. `ui/hud/MissionList.jsx` renders ONLY under the pause
      // menu, which has already set the time scale to 0, so nothing will tick to
      // drain the flag: the Track button repainted NOTHING in the shipped game.
      // A bus emit is a function call, not a tick. (The `'shop:state'`-inside-
      // `buy()` rule, one system over.)
      publish();
    }));
    offs.push(bus.on('player:respawn', () => {
      if (disposed) return;
      // The WASTED loop already failed anything active (the `failOnWasted` rule
      // runs on the step `player.dead` goes true). What is left is the retry
      // clock, which should start from the respawn rather than from the death —
      // a player watching a three-second splash has not been offered anything.
      for (const m of missions) if (m.state === MISSION_STATE.FAILED) m.retryT = 0;
    }));
  }

  relock();

  return {
    name: 'missions',
    followers,
    update,
    markers,
    worldMarkers,

    /** @returns {string} The ACTIVE mission's id, or ''. */
    get active() {
      return activeId;
    },
    /** @returns {string} The TRACKED mission's id, or ''. */
    get tracked() {
      return trackedId;
    },

    /**
     * The mission list's toggle, reachable without a bus (tests, a debug key).
     * @param {string} id @returns {boolean}
     */
    track(id) {
      const m = byId(id);
      if (!m || m.state === MISSION_STATE.LOCKED || m.state === MISSION_STATE.COMPLETE) return false;
      trackedId = id;
      dirty = true;
      return true;
    },

    /** @param {string} id @returns {string} {@link MISSION_STATE} */
    stateOf(id) {
      return byId(id)?.state ?? '';
    },

    /** @param {string} id @returns {number} Stage index, -1 before the first. */
    stageOf(id) {
      return byId(id)?.stage ?? -1;
    },

    /** @returns {string} The tracked mission's HUD line. */
    objectiveText,

    /** `Gameplay`'s player hot-swap points us — and the escort — at the
     *  replacement actor. */
    setPlayer(p) {
      if (!p) return;
      player = p;
      followers.setPlayer(p);
    },

    /**
     * Ends the active mission from outside — a debug key, a cheat, a level
     * teardown that wants the books straight.
     * @param {boolean} [win=false]
     */
    endActive(win = false) {
      const m = activeId ? byId(activeId) : null;
      if (!m) return;
      if (win) complete(m);
      else fail(m, 'aborted');
    },

    /**
     * A manager slot went free, for ANY reason. Wired into
     * `EnemyManager#onDespawn` by `Gameplay`, beside `Population`'s — so a
     * recycled slot can never be counted as a mission kill or take an escort's
     * orders.
     * @param {number} slot
     */
    onSlotFreed(slot) {
      followers.onSlotFreed(slot);
      for (const m of missions) {
        for (let i = m.actors.length - 1; i >= 0; i--) {
          if (m.actors[i].slot !== slot) continue;
          // Latch the death BEFORE dropping the record: a hostile whose slot
          // freed without ever being seen DYING (a cull, a `clear()`) still has
          // to count, or `eliminate` waits forever for a man who is gone.
          m.actors[i].down = true;
          m.actors.splice(i, 1);
          dirty = true;
        }
      }
    },

    /** @returns {Object} Live counters for the debug read-out. */
    stats() {
      const byState = {};
      for (const m of missions) byState[m.state] = (byState[m.state] ?? 0) + 1;
      const m = activeId ? byId(activeId) : null;
      return {
        active: activeId || null,
        tracked: trackedId || null,
        stage: m ? `${m.stage + 1}/${m.def.stages.length}` : null,
        kind: m ? m.def.stages[m.stage]?.kind ?? null : null,
        killed: m ? `${m.killed}/${m.quota}` : null,
        actors: missions.reduce((n, x) => n + x.actors.length, 0),
        followers: followers.stats(),
        started,
        completed,
        failed,
        byState,
      };
    },

    /** Exposed for the tests + the debug panel. */
    get missions() {
      return missions;
    },

    /** Idempotent. Frees every actor this director put in the world. */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const off of offs) off?.();
      offs.length = 0;
      // Nothing may ring on the way out: a torn-down director has no world left
      // to offer a job in, and the queue is the one piece of state here that
      // would otherwise fire on somebody else's next step.
      offerQ.length = 0;
      // Followers first: it has to hand its passengers out of the car while the
      // driving system it borrowed the seats from is still alive.
      followers.dispose();
      for (const m of missions) {
        // The same snapshot rule as `clearActors` — `onSlotFreed` splices.
        const doomed = m.actors.slice();
        m.actors.length = 0;
        for (const a of doomed) if (ours(a)) manager.despawn(a.slot);
        // ...and take any briefing down with us. A still image left on screen
        // over a frozen loop, with no director left to answer it, is the one
        // teardown bug the player cannot recover from.
        cancelCutscene(m);
      }
      activeId = '';
    },
  };
}
